import React, { useEffect, useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import useToolPromptsStore from '../../store/tool-prompts-store';
import useToolStore from '../../store/tools-store';
import useModelStore from '../../store/models-store';
import useCrudListPage from '../../shared/hooks/useCrudListPage';
import DataTable from '../../shared/components/table/DataTable';
import PageHeader from '../../shared/components/table/PageHeader';
import SearchInput from '../../shared/components/table/SearchInput';
import RowActions from '../../shared/components/table/RowActions';
import ResourceFormModal from '../../shared/components/modal/ResourceFormModal';
import DeleteModal from '../../shared/components/modal/DeleteModal';
import { renderLargeCell } from '../../shared/lib/renderLargeCell';

const columnHelper = createColumnHelper();

const ToolPromptsPage = () => {
  const { toolPrompts, fetchAllToolPrompts, createToolPrompt, updateToolPrompt, deleteToolPrompt } =
    useToolPromptsStore();
  const { tools, fetchTools } = useToolStore();
  const { models, fetchModels } = useModelStore();
  const [expandedRows, setExpandedRows] = useState({});
  const openEditRef = React.useRef(() => {});
  const openDeleteRef = React.useRef(() => {});

  const modelsForSelect = useMemo(
    () =>
      (models || []).map((model) => ({
        ...model,
        name: model.reasoning_effort ? `${model.name} (${model.reasoning_effort})` : model.name,
      })),
    [models]
  );

  useEffect(() => {
    fetchTools();
    fetchModels();
  }, [fetchTools, fetchModels]);

  const toolOptions = useMemo(
    () =>
      [...(tools || [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((t) => ({ value: t.id, label: t.name })),
    [tools]
  );

  const modelOptions = useMemo(
    () =>
      [...modelsForSelect]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((m) => ({ value: m.id, label: m.name })),
    [modelsForSelect]
  );

  const fields = useMemo(
    () => [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'precedence', label: 'Precedence', type: 'number', required: true },
      { name: 'value', label: 'Value', type: 'textarea', required: true, rows: 8, colSpan: 2 },
      {
        name: 'tool_id',
        label: 'Tool',
        type: 'select',
        required: true,
        options: toolOptions,
        placeholder: 'Select Tool',
      },
      {
        name: 'model_id',
        label: 'Model',
        type: 'select',
        required: true,
        options: modelOptions,
        placeholder: 'Select Model',
      },
    ],
    [toolOptions, modelOptions]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', { header: 'ID' }),
      columnHelper.accessor('name', { header: 'Name' }),
      columnHelper.accessor('value', {
        header: 'Value',
        cell: (info) => renderLargeCell(expandedRows, setExpandedRows, info, 'value'),
      }),
      columnHelper.accessor('precedence', { header: 'Precedence' }),
      columnHelper.accessor('tool_name', { header: 'Tool Name' }),
      columnHelper.accessor('model_name', {
        header: 'Model Name',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
      }),
      columnHelper.accessor('actions', {
        header: 'Actions',
        cell: ({ row }) => (
          <RowActions
            onEdit={() => openEditRef.current(row.original)}
            onDelete={() => openDeleteRef.current(row.original)}
          />
        ),
      }),
    ],
    [expandedRows]
  );

  const {
    table,
    isLoading,
    globalFilter,
    setGlobalFilter,
    isFormOpen,
    formMode,
    isDeleteModalOpen,
    selectedItem,
    deletePayload,
    openCreate,
    openEdit,
    openDelete,
    closeModals,
    handleFormSubmit,
    handleDelete,
  } = useCrudListPage({
    items: toolPrompts,
    fetchItems: fetchAllToolPrompts,
    createItem: createToolPrompt,
    updateItem: updateToolPrompt,
    deleteItem: deleteToolPrompt,
    columns,
    entityLabel: 'Tool Prompt',
    getDeletePayload: (item) => ({ ...item, dataName: 'tool prompt' }),
  });

  openEditRef.current = openEdit;
  openDeleteRef.current = openDelete;

  const initialValues = useMemo(() => {
    if (formMode === 'edit' && selectedItem) {
      return {
        name: selectedItem.name || '',
        value: selectedItem.value || '',
        precedence: selectedItem.precedence ?? 0,
        tool_id: selectedItem.tool_id || '',
        model_id: selectedItem.model_id || '',
      };
    }
    return {
      name: '',
      value: '',
      precedence: 0,
      tool_id: '',
      model_id: '',
    };
  }, [formMode, selectedItem]);

  return (
    <div className="max-w-screen-3xl mx-auto mt-4">
      <PageHeader title="Tool Prompts List" onCreate={openCreate} />
      <div className="mb-4">
        <SearchInput value={globalFilter} onChange={setGlobalFilter} />
      </div>
      <DataTable
        table={table}
        columnsCount={columns.length}
        isLoading={isLoading}
        emptyMessage="No tool prompts found"
      />
      <ResourceFormModal
        isOpen={isFormOpen}
        onClose={closeModals}
        mode={formMode}
        title={formMode === 'edit' ? 'Edit Tool Prompt' : 'Create Tool Prompt'}
        fields={fields}
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        gridClassName="grid grid-cols-2 gap-2"
      />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={closeModals}
        data={deletePayload}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default ToolPromptsPage;
